// Stage-based system scheduler. Replaces the imperative call list main.ts
// used to hand-order. Adding a system is one entry in the pipeline array;
// gates (gallery/shop/death) live in `enabled` predicates.
//
// Stages run in declared order. Within a stage, `order` (default 0) breaks
// ties — used sparingly when one system must run before another in the same
// stage (e.g. movement before collision in 'simulation').

import type { GameWorld } from './world'
import type { Entity } from './components'
import type { GameInput } from './controls'
import type { Camera } from '../engine/camera'
import type { EventBus } from './events'

export type Stage =
  | 'input'
  | 'ai'
  | 'simulation'
  | 'collision'
  | 'effects'
  | 'spawning'
  | 'cleanup'
  | 'camera'
  | 'render'
  | 'hud'

export const STAGES: readonly Stage[] = [
  'input', 'ai', 'simulation', 'collision', 'effects',
  'spawning', 'cleanup', 'camera', 'render', 'hud',
]

// Stage subsets the frame loop feeds to runStages. Exported (not main.ts
// locals) so tests can pin their contents — the 'input' stage silently never
// running is exactly the regression to guard against.
export const GAMEPLAY_STAGES: ReadonlySet<Stage> = new Set(['input', 'ai', 'simulation', 'collision', 'effects'])
export const SPAWN_STAGES: ReadonlySet<Stage> = new Set(['spawning'])
export const CAMERA_STAGES: ReadonlySet<Stage> = new Set(['camera'])

export interface FrameCtx {
  world: GameWorld
  dt: number
  elapsed: number
  now: number
  input: GameInput
  camera: Camera
  events: EventBus
  /** Current player. Mutable wrapper because respawn replaces the entity. */
  playerRef: { current: Entity }
}

export interface System {
  name: string
  stage: Stage
  /** Default 0. Lower runs first within the stage. */
  order?: number
  /** Skip this system if predicate returns false. */
  enabled?: (ctx: FrameCtx) => boolean
  run: (ctx: FrameCtx) => void
}

/** Sort systems into stage order (stable within stage; ties broken by `order`). */
export function compilePipeline(systems: System[]): System[] {
  const stageIdx: Record<Stage, number> = STAGES.reduce((acc, s, i) => {
    acc[s] = i
    return acc
  }, {} as Record<Stage, number>)
  return [...systems].sort((a, b) => {
    const ds = stageIdx[a.stage] - stageIdx[b.stage]
    if (ds !== 0) return ds
    return (a.order ?? 0) - (b.order ?? 0)
  })
}

/** Run every enabled system in pipeline order. */
export function runPipeline(ctx: FrameCtx, pipeline: readonly System[]): void {
  for (const sys of pipeline) {
    if (sys.enabled && !sys.enabled(ctx)) continue
    sys.run(ctx)
  }
}

/** Run only systems in the given stages. Useful for partial frames (e.g. paused, dead). */
export function runStages(
  ctx: FrameCtx,
  pipeline: readonly System[],
  stages: ReadonlySet<Stage>,
): void {
  for (const sys of pipeline) {
    if (!stages.has(sys.stage)) continue
    if (sys.enabled && !sys.enabled(ctx)) continue
    sys.run(ctx)
  }
}
